import React, { useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Fancybox from '@@components/Fancybox/Fancybox';
import KeyToSymbol, { TKeyToSymbol } from '@@components/KeyToSymbol/KeyToSymbol';
import { TAnnotatedDataPoint } from '@@config/report';
import SingleTooltip from '@@components/SingleTooltip/SingleTooltip';

type TTableRowProps = {
    tableItemKey: string;
    tableItemData: TAnnotatedDataPoint;
    keyToSymbols?: TKeyToSymbol[];
};

/**
 * Image field keys that render on their own full-width row instead of the right-hand value
 * column used by inline images like CIIMG. Uses the codebase's full-row idiom — a single
 * colSpan={2} cell (see OriginImageRows / InscriptionRow) — which, combined with
 * `td:only-child { width: 100% }` in Table/index.scss, spans the full table width.
 * Add future full-bleed images (reference scales, charts) here.
 */
const FULL_WIDTH_IMAGE_KEYS: ReadonlySet<string> = new Set(['LUSTER_SCALE_IMAGE']);

const TableRow: React.FC<TTableRowProps> = ({ tableItemKey, tableItemData, keyToSymbols }) => {
    const { KIND, LABEL, TOOLTIP, VALUE } = tableItemData;

    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipId = useMemo(() => uuidv4(), []);

    const handleMouseEnter = useCallback(() => setShowTooltip(true), []);
    const handleMouseLeave = useCallback(() => setShowTooltip(false), []);

    if (KIND === 'image' && FULL_WIDTH_IMAGE_KEYS.has(tableItemKey)) {
        return (
            <tr className='bg-white'>
                <td colSpan={2}>
                    <Fancybox>
                        <a data-fancybox href={VALUE}>
                            <img className='table-image' id={tableItemKey} src={VALUE} alt={LABEL} />
                        </a>
                    </Fancybox>
                </td>
            </tr>
        );
    }

    if (KIND === 'image' && tableItemKey === 'PLOTIMG' && keyToSymbols?.length) {
        return (
            <tr className='bg-white'>
                <td className=''>
                    <div className='flex flex-col items-baseline'>
                        {keyToSymbols.map((kts: TKeyToSymbol, i: number) => (
                            <KeyToSymbol key={`${i}:${kts.imageName}`} imageName={kts.imageName} label={kts.label} />
                        ))}
                    </div>
                </td>
                <td className='text-right'>
                    <Fancybox>
                        <a data-fancybox href={VALUE}>
                            <img className='table-image' id={tableItemKey} src={VALUE} alt={LABEL} />
                        </a>
                    </Fancybox>
                </td>
            </tr>
        );
    }

    if (KIND === 'image') {
        return (
            <tr className='bg-white'>
                <td>&nbsp;</td>
                <td className='text-right'>
                    <Fancybox>
                        <a data-fancybox href={VALUE}>
                            <img className='table-image' id={tableItemKey} src={VALUE} alt={LABEL} />
                        </a>
                    </Fancybox>
                </td>
            </tr>
        );
    }

    if (KIND === 'data' && LABEL === 'Disclaimer Text') {
        return (
            <tr className='bg-white'>
                <td>&nbsp;</td>
                <td className='text-right'>{VALUE}</td>
            </tr>
        );
    }

    return (
        <tr>
            {TOOLTIP && (
                <th scope='row' className='relative'>
                    <span
                        data-test-id={`${tableItemKey}_LABEL`}
                        className='single-tooltip-label'
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        {LABEL}
                    </span>
                    {showTooltip && (
                        <SingleTooltip
                            tooltipId={tooltipId}
                            tooltipContent={TOOLTIP}
                            handleMouseEnter={handleMouseEnter}
                            handleMouseLeave={handleMouseLeave}
                        />
                    )}
                </th>
            )}
            <td colSpan={TOOLTIP ? 1 : 2}>
                <span id={tableItemKey}>{VALUE}</span>
            </td>
        </tr>
    );
};

export default TableRow;
